import {
  DEFAULT_BEVEL_CUT_TYPE_PREFERENCE,
  DEFAULT_ORGANIZATION_SETTINGS,
  DistanceFormat,
  UserSettings,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';

export async function getFallbackUserSettings(
  userOrganization: string,
  userPermissions: readonly string[],
): Promise<UserSettings> {
  const organizationSettings = (await designService.settings.getOrganizationSettings()) ?? {
    ...DEFAULT_ORGANIZATION_SETTINGS,
    organization: userOrganization,
  };

  return getDefaultUserSettings(userPermissions, organizationSettings.useDistanceFormat);
}

// Corresponds to the server side method `UserSettings.Default` in `DesignService.Models`
function getDefaultUserSettings(permissions: readonly string[], distanceFormat: DistanceFormat): UserSettings {
  if (permissions.includes('free')) {
    return {
      cameraSettings: {
        flipped: false,
        reversed: false,
      },
      dimensionSettings: {
        showGrid: false,
        showStandardDimensions: true,
        dimensionLabelType: 'Combined',
        visibleDimensionCategory: 'Minimal',
        showPitch: true,
        showSelectedMemberCutAngles: true,
        showSelectedMemberLengthDimensions: true,
      },
      viewOptionSettings: {
        colorBy: 'LumberProperties',
        showPlates: true,
        showPlateNames: false,
        plateDetailSettings: {
          showPlateDetails: true,
          hideForMostCommonSize: false,
          hideForMostCommonType: false,
        },
        memberLabelOptions: {
          nonWebLabelContent: 'Hidden',
          webLabelContent: 'Hidden',
        },
        snapSettings: {
          showCentroidSnaps: true,
          showIntersectionSnaps: true,
          showMidLineSnaps: true,
          showVertexSnaps: true,
        },
        distanceFormat,
        highVisibilityMode: true,
      },
      layoutSettings: {
        bevelCutTypePreference: DEFAULT_BEVEL_CUT_TYPE_PREFERENCE,
      },
    };
  }

  return {
    cameraSettings: {
      flipped: false,
      reversed: false,
    },
    dimensionSettings: {
      showGrid: true,
      showStandardDimensions: true,
      dimensionLabelType: 'Combined',
      visibleDimensionCategory: 'General',
      showPitch: !permissions.includes('assembly'),
      showSelectedMemberCutAngles: true,
      showSelectedMemberLengthDimensions: true,
    },
    viewOptionSettings: {
      colorBy: 'LumberProperties',
      showPlates: true,
      showPlateNames: true,
      plateDetailSettings: {
        showPlateDetails: permissions.includes('assembly'),
        hideForMostCommonSize: false,
        hideForMostCommonType: permissions.includes('assembly'),
      },
      memberLabelOptions: {
        nonWebLabelContent: 'MemberName',
        webLabelContent: 'MemberName',
      },
      snapSettings: {
        showCentroidSnaps: true,
        showIntersectionSnaps: true,
        showMidLineSnaps: true,
        showVertexSnaps: true,
      },
      distanceFormat,
      highVisibilityMode: false,
    },
    layoutSettings: {
      bevelCutTypePreference: DEFAULT_BEVEL_CUT_TYPE_PREFERENCE,
    },
  };
}
